
import Link from '@/components/compat/Link';
import { ChevronDown, ExternalLink, LogOut, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  BUILTIN_ROLE_VISUALS,
  PainelRoleBadge,
  PainelRoleIcon,
} from '@/components/painel/permissions/painel-role-visuals';
import { painelPermissionsModalShellClass } from '@/components/painel/painel-styles';
import { adminLogout } from '@/lib/admin-auth';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { PAINEL_ROLE_LABELS, type PainelRole } from '@/lib/painel-rbac';
import { resolveMediaUrl } from '@/services/api-client';
import { cn } from '@/lib/utils';

function userInitials(name: string) {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

type UserAvatarProps = {
  name: string;
  avatarUrl?: string | null;
  role: PainelRole;
  size?: 'sm' | 'md';
  className?: string;
};

function UserAvatar({ name, avatarUrl, role, size = 'sm', className }: UserAvatarProps) {
  const roleVisual = BUILTIN_ROLE_VISUALS[role];
  const initials = userInitials(name);
  const resolvedUrl = resolveMediaUrl(avatarUrl);
  const sizeClass = size === 'md' ? 'h-11 w-11 text-sm' : 'h-9 w-9 text-sm';

  return (
    <span
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg font-poppins font-bold',
        'ring-1 ring-white/15',
        roleVisual.iconShell,
        roleVisual.glow,
        sizeClass,
        className
      )}
    >
      {resolvedUrl ? (
        <img src={resolvedUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className={cn('relative z-[1]', roleVisual.iconColor)}>{initials}</span>
      )}
      {!resolvedUrl ? (
        <span
          className="pointer-events-none absolute inset-0 rounded-[inherit] bg-gradient-to-br from-white/10 to-transparent"
          aria-hidden
        />
      ) : null}
    </span>
  );
}

const menuItemClass = cn(
  'group/item relative flex w-full items-center gap-3 px-4 py-2.5 text-left',
  'font-inter text-sm text-slate-200 transition-colors duration-200',
  'hover:bg-blue-950/45 focus-visible:outline-none focus-visible:bg-blue-950/50'
);

const menuIconShellClass = cn(
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
  'bg-blue-950/55 ring-1 ring-white/10 transition-all duration-200',
  'group-hover/item:bg-blue-950/75 group-hover/item:ring-sky-400/25'
);

function UserMenuSkeleton() {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.04] py-1.5 pl-1.5 pr-2.5',
        'pointer-events-none'
      )}
      aria-hidden
    >
      <div className="h-9 w-9 shrink-0 animate-pulse rounded-lg bg-white/10" />
      <div className="hidden min-w-0 space-y-1.5 sm:block">
        <div className="h-3 w-[4.5rem] animate-pulse rounded bg-white/10" />
        <div className="h-[18px] w-[5.5rem] animate-pulse rounded-full bg-white/10" />
      </div>
      <div className="hidden h-4 w-4 shrink-0 animate-pulse rounded bg-white/10 sm:block" />
    </div>
  );
}

export function PainelUserMenu() {
  const { session, role } = usePainelSession();

  if (!role) {
    return <UserMenuSkeleton />;
  }

  const displayName = session?.name ?? 'Admin';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'group relative flex items-center gap-2.5 rounded-xl py-1.5 pl-1.5 pr-2.5',
            'border border-white/10 bg-gradient-to-b from-white/[0.08] to-white/[0.03]',
            'shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]',
            'transition-all duration-200',
            'hover:border-sky-400/30 hover:from-white/[0.11] hover:to-white/[0.05]',
            'hover:shadow-[0_0_24px_-8px_rgba(56,189,248,0.35)]',
            'outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0',
            'data-[state=open]:border-sky-400/40 data-[state=open]:from-white/[0.12] data-[state=open]:to-white/[0.06]',
            'data-[state=open]:shadow-[0_0_28px_-6px_rgba(56,189,248,0.4)]'
          )}
          aria-label="Menu da conta"
        >
          <UserAvatar name={displayName} avatarUrl={session?.avatarUrl} role={role} />

          <span className="hidden min-w-0 text-left sm:block">
            <span className="block truncate font-poppins text-xs font-semibold text-blue-50">
              {displayName}
            </span>
            <span className="mt-1 block">
              <PainelRoleBadge role={role} compact />
            </span>
          </span>

          <ChevronDown
            className={cn(
              'hidden h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200 sm:block',
              'group-data-[state=open]:rotate-180 group-data-[state=open]:text-sky-300/80'
            )}
            aria-hidden
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={10}
        onCloseAutoFocus={(event) => event.preventDefault()}
        className={cn(
          'w-[min(100vw-2rem,300px)] overflow-hidden border-0 bg-transparent p-0 shadow-none',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98]'
        )}
      >
        <div className={cn('relative', painelPermissionsModalShellClass)}>
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#030023]/40 via-[#000b6a]/20 to-[#030023]/50"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(ellipse_80%_55%_at_50%_-20%,rgba(56,189,248,0.12),transparent)]"
            aria-hidden
          />

          <div className="relative border-b border-white/10 bg-blue-950/20 px-4 py-4">
            <div className="flex items-start gap-3">
              {session?.avatarUrl ? (
                <UserAvatar
                  name={displayName}
                  avatarUrl={session.avatarUrl}
                  role={role}
                  size="md"
                />
              ) : (
                <PainelRoleIcon config={BUILTIN_ROLE_VISUALS[role]} size="md" />
              )}
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="truncate font-poppins text-sm font-bold text-white">{displayName}</p>
                <div className="mt-1.5">
                  <PainelRoleBadge role={role} />
                </div>
              </div>
            </div>
          </div>

          <ul className="relative divide-y divide-white/[0.06] bg-[#030023]/30 py-1">
            <li>
              <Link href="/painel/configuracoes" className={menuItemClass}>
                <span className={menuIconShellClass}>
                  <Settings className="h-4 w-4 text-sky-300" aria-hidden />
                </span>
                <span>
                  <span className="block font-poppins text-[13px] font-semibold text-blue-50">
                    Configurações da conta
                  </span>
                  <span className="mt-0.5 block font-inter text-[11px] text-slate-500">
                    Perfil, senha e preferências
                  </span>
                </span>
              </Link>
            </li>
            <li>
              <Link href="/" className={menuItemClass}>
                <span className={menuIconShellClass}>
                  <ExternalLink className="h-4 w-4 text-sky-300" aria-hidden />
                </span>
                <span>
                  <span className="block font-poppins text-[13px] font-semibold text-blue-50">
                    Ir ao site
                  </span>
                  <span className="mt-0.5 block font-inter text-[11px] text-slate-500">
                    Abrir o Help Center público
                  </span>
                </span>
              </Link>
            </li>
          </ul>

          <div className="relative border-t border-white/10 bg-blue-950/25 p-1.5">
            <button
              type="button"
              onClick={() => adminLogout()}
              className={cn(
                menuItemClass,
                'rounded-xl text-rose-200 hover:bg-rose-500/10 hover:text-rose-100',
                'focus-visible:bg-rose-500/10'
              )}
            >
              <span
                className={cn(
                  menuIconShellClass,
                  'bg-rose-950/40 ring-rose-400/20',
                  'group-hover/item:bg-rose-950/55 group-hover/item:ring-rose-400/30'
                )}
              >
                <LogOut className="h-4 w-4 text-rose-300" aria-hidden />
              </span>
              <span>
                <span className="block font-poppins text-[13px] font-semibold">Logout</span>
                <span className="mt-0.5 block font-inter text-[11px] text-rose-300/60">
                  Terminar sessão no painel
                </span>
              </span>
            </button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
